import { useEffect, useRef } from 'react';
import { focusManager } from '@tanstack/react-query';
import {
  ACTIVE_USER_WINDOW_MS,
  consumeTabHiddenDurationMs,
  ensureSessionFresh,
  markTabVisibilityState,
} from '@/lib/sessionRefresh';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
const REFRESH_CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Keeps the auth session fresh during active use and silently renews it when the user returns to the tab.
 */
export function SessionRefreshManager(): null {
  const lastActivityAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const markActivity = (): void => {
      lastActivityAtRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });

    const checkAndRefreshSession = async (): Promise<void> => {
      const isActiveUser = Date.now() - lastActivityAtRef.current <= ACTIVE_USER_WINDOW_MS;
      try {
        await ensureSessionFresh({ reason: 'proactive', isActiveUser });
      } catch {
        // Interceptor handles hard auth failures on the next API call.
      }
    };

    const handleTabResume = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      markActivity();
      const hiddenDurationMs = consumeTabHiddenDurationMs();

      try {
        await ensureSessionFresh({
          reason: 'tab-resume',
          tabHiddenDurationMs: hiddenDurationMs,
        });
      } catch {
        // Interceptor handles hard auth failures on the next API call.
      }
    };

    focusManager.setEventListener((handleFocus) => {
      const onResume = (): void => {
        void handleTabResume().finally(() => {
          handleFocus();
        });
      };

      const onVisibilityChange = (): void => {
        markTabVisibilityState(document.visibilityState);
        if (document.visibilityState === 'visible') {
          onResume();
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', onResume);

      if (document.visibilityState === 'hidden') {
        markTabVisibilityState('hidden');
      }

      if (document.visibilityState === 'visible') {
        handleFocus();
      }

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('focus', onResume);
      };
    });

    void checkAndRefreshSession();
    const intervalId = window.setInterval(() => {
      void checkAndRefreshSession();
    }, REFRESH_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      focusManager.setEventListener((handleFocus) => {
        const onVisibility = (): void => {
          if (document.visibilityState === 'visible') {
            handleFocus();
          }
        };

        document.addEventListener('visibilitychange', onVisibility);
        return () => {
          document.removeEventListener('visibilitychange', onVisibility);
        };
      });
    };
  }, []);

  return null;
}
